"use client";

import { useUser } from "@clerk/nextjs";
import { Drawer } from "@repo/ui/components/drawer";
import { IconLifebuoy, IconLogout2 } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import IconMenu from "@/assets/icons/burger-menu.svg";
import { LanguageSwitcher } from "@/components/header/language";
import { MobileMenuBenefits } from "@/components/header/mobile-menu-benefits";
import { MobileMenuLink } from "@/components/header/mobile-menu-link";
import { MobileMenuUser } from "@/components/header/mobile-menu-user";
import { SheetClose } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { getAccountMenuLinks } from "@/data/account-menu";
import { getMobileMenuLinks } from "@/data/mobile-menu";
import { useSignOut } from "@/features/auth/hooks/use-sign-out";
import { cn } from "@/lib/utils";
import { type HeaderThemeType } from "@/types/common.types";

const Divider = () => (
  <div className="px-4 py-2">
    <span aria-hidden="true" className="block h-px w-full bg-border" />
  </div>
);

export const MobileNavigation = ({
  className,
  theme,
}: {
  className?: string;
  theme?: HeaderThemeType;
}) => {
  const t = useTranslations("header");
  const { isLoaded, isSignedIn } = useUser();
  const menuLinks = getMobileMenuLinks();
  const accountLinks = getAccountMenuLinks();
  const signOut = useSignOut();

  return (
    <div
      className={cn("flex items-center", className)}
      data-pw="drawer-menu-button"
    >
      <Drawer
        className="sm:max-w-none [&>button>svg]:size-6 [&>button]:top-4 [&>button]:right-4 [&>button]:left-auto"
        contentClassName="flex-col p-0"
        cover
        headerSheetClassName="h-14 justify-center border-border py-0"
        headerTitleClassName="text-xl leading-7"
        side="right"
        title={t("menu.title")}
        trigger={{
          label: (
            <IconMenu
              className={cn(
                (theme === "light" || theme === "all-light") &&
                  "[&_path]:fill-black"
              )}
            />
          ),
          variant: "ghost",
          className: "px-0 min-h-8 py-2 transition-none",
        }}
      >
        <nav className="flex w-full flex-col" data-pw="drawer-menu">
          {isLoaded ? (
            <>
              {isSignedIn ? <MobileMenuUser /> : <MobileMenuBenefits />}
              {isSignedIn && <Divider />}
              {(isSignedIn ? accountLinks : menuLinks).map((link) => (
                <MobileMenuLink key={link.label} {...link} />
              ))}
              <MobileMenuLink
                href="/help-center"
                icon={
                  <IconLifebuoy className="size-6 shrink-0" strokeWidth={1.5} />
                }
                label={t("helpCenter")}
                testData="mobile-menu-help-center"
              />
              {isSignedIn && (
                <>
                  <Divider />
                  <SheetClose asChild>
                    <button
                      className="flex w-full items-center gap-2 rounded-lg p-4 text-base text-text leading-6"
                      data-pw="mobile-drawer-sign-out"
                      onClick={signOut}
                      type="button"
                    >
                      <IconLogout2 className="size-6 shrink-0" strokeWidth={1.5} />
                      <span>{t("signOut")}</span>
                    </button>
                  </SheetClose>
                </>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-2 p-4">
              <Skeleton className="h-40 w-full rounded-xl" />
              <Skeleton className="h-14 w-full rounded-lg" />
              <Skeleton className="h-14 w-full rounded-lg" />
            </div>
          )}
        </nav>
        {/* Not in the design frame, but the drawer is the only place to
            switch languages on mobile. */}
        <div className="mt-auto px-2 py-4">
          <LanguageSwitcher />
        </div>
      </Drawer>
    </div>
  );
};
